package com.cs.ejobin.ui.screens.profile

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.cs.ejobin.i18n.Keys
import com.cs.ejobin.i18n.t
import com.cs.ejobin.model.SkillCatalog
import com.cs.ejobin.ui.components.AvatarImage
import com.cs.ejobin.ui.components.ErrorText
import com.cs.ejobin.ui.components.ScreenScaffold
import com.cs.ejobin.ui.components.SkillCatalogChips
import com.cs.ejobin.ui.picker.rememberImagePicker
import com.cs.ejobin.ui.theme.AppColors
import com.cs.ejobin.ui.theme.AppSurfaces
import com.cs.ejobin.vm.ProfileViewModel
import kotlin.random.Random

@Composable
fun ProfileScreen(
    vm: ProfileViewModel,
    onTopHome: () -> Unit,
    onOpenPaywall: () -> Unit, // ✅ conservé pour ne rien casser, mais plus utilisé
    onLoggedOut: () -> Unit,
    onTopLogout: () -> Unit
) {
    val state by vm.state.collectAsState()

    LaunchedEffect(Unit) {
        vm.bindFromSession()
        vm.loadProfile()
    }

    val user = state.user

    val primaryTextButtonColors = ButtonDefaults.textButtonColors(
        contentColor = AppColors.Primary
    )

    ScreenScaffold(
        title = t(Keys.profile_title),
        topActions = {
            TextButton(
                onClick = onTopHome,
                enabled = !state.isLoading,
                colors = primaryTextButtonColors
            ) { Text(t(Keys.home)) }
            TextButton(
                onClick = onTopLogout,
                enabled = !state.isLoading,
                colors = primaryTextButtonColors
            ) { Text(t(Keys.logout)) }
        },
        scrollable = true
    ) {
        if (user == null) {
            if (state.isLoading) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                    Text(t(Keys.loading))
                }
            } else {
                Text(t(Keys.not_connected))
            }
            ErrorText(state.errorKey)
            return@ScreenScaffold
        }

        var name by remember(user.id) { mutableStateOf(user.name) }
        var phone by remember(user.id) { mutableStateOf(user.phone) }
        var available by remember(user.id) { mutableStateOf(user.isAvailable) }
        var description by remember(user.id) { mutableStateOf(user.description.orEmpty()) }

        var selectedSkills by remember(user.id) {
            val selected = user.skills
                .mapNotNull { s -> SkillCatalog.entries.firstOrNull { it.label == s.label } }
                .toSet()
            mutableStateOf(selected)
        }

        val avatarUrl = user.photoUrl?.let { "$it?ts=${Random.nextInt()}" }

        val pickImage = rememberImagePicker { picked ->
            vm.updateAvatar(
                pickedBytes = picked.bytes,
                contentType = picked.contentType,
                fileExt = picked.fileExt,
                currentName = name,
                currentPhone = phone,
                currentAvailable = available,
                currentSkills = selectedSkills,
                currentDescription = description
            )
        }

        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.Center) {
            AvatarImage(url = avatarUrl, size = 110.dp, cornerRadius = 18.dp)
        }

        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = AppSurfaces.glassCard(),
            border = CardDefaults.outlinedCardBorder()
        ) {
            Column(
                modifier = Modifier.padding(14.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Button(
                    onClick = pickImage,
                    enabled = !state.isLoading,
                    modifier = Modifier.fillMaxWidth(),
                    colors = ButtonDefaults.buttonColors(containerColor = AppColors.Primary)
                ) { Text(t(Keys.pick_photo)) }

                Text("${t(Keys.email_label)} ${user.email}", color = AppColors.TextPrimary)
                Text("${t(Keys.phone_label)} ${user.phone}", color = AppColors.TextPrimary)
            }
        }

        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = AppSurfaces.glassCard(),
            border = CardDefaults.outlinedCardBorder()
        ) {
            Column(
                modifier = Modifier.padding(14.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                OutlinedTextField(
                    value = name,
                    onValueChange = { name = it },
                    label = { Text(t(Keys.name)) },
                    modifier = Modifier.fillMaxWidth(),
                    enabled = !state.isLoading,
                    singleLine = true
                )

                OutlinedTextField(
                    value = phone,
                    onValueChange = { phone = it },
                    label = { Text(t(Keys.phone)) },
                    modifier = Modifier.fillMaxWidth(),
                    enabled = !state.isLoading,
                    singleLine = true
                )

                Row(
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(t(Keys.available), color = AppColors.TextPrimary)
                    Switch(
                        checked = available,
                        onCheckedChange = { available = it },
                        enabled = !state.isLoading,
                        colors = SwitchDefaults.colors(checkedTrackColor = AppColors.Primary)
                    )
                }

                OutlinedTextField(
                    value = description,
                    onValueChange = { description = it },
                    label = { Text(t(Keys.description)) },
                    modifier = Modifier.fillMaxWidth(),
                    enabled = !state.isLoading,
                    minLines = 3,
                )

                SkillCatalogChips(
                    title = t(Keys.skills_jobs),
                    all = SkillCatalog.entries,
                    selected = selectedSkills,
                    onToggle = { skill ->
                        val set = selectedSkills.toMutableSet()
                        if (!set.add(skill)) set.remove(skill)
                        selectedSkills = set
                    },
                    enabled = !state.isLoading,
                    columns = 3,
                    collapsedCount = 9
                )

                Button(
                    onClick = {
                        vm.updateProfile(
                            name = name,
                            phone = phone,
                            isAvailable = available,
                            selectedSkills = selectedSkills,
                            photoUrl = user.photoUrl,
                            description = description
                        )
                    },
                    enabled = !state.isLoading,
                    modifier = Modifier.fillMaxWidth(),
                    colors = ButtonDefaults.buttonColors(containerColor = AppColors.Primary)
                ) {
                    Text(if (state.isLoading) t(Keys.saving) else t(Keys.save))
                }

                ErrorText(state.errorKey)
            }
        }

        // ✅ Paiement désactivé : on garde la card en commentaire (pour remettre plus tard)
        /*
        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = AppSurfaces.glassCard(),
            border = CardDefaults.outlinedCardBorder()
        ) {
            Column(
                modifier = Modifier.padding(14.dp),
                verticalArrangement = Arrangement.spacedBy(10.dp)
            ) {
                val sub = state.subscription
                val subLabelKey = when (sub) {
                    Subscription.None -> Keys.subscription_none
                    is Subscription.Active -> if (sub.isActive) Keys.subscription_active else Keys.subscription_expired
                }

                Text("${t(Keys.subscription_status)} ${t(subLabelKey)}", color = AppColors.TextPrimary)

                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    Button(
                        onClick = onOpenPaywall,
                        enabled = !state.isLoading,
                        colors = ButtonDefaults.buttonColors(containerColor = AppColors.Primary)
                    ) { Text(t(Keys.see_offers)) }

                    OutlinedButton(
                        onClick = vm::refreshSubscription,
                        enabled = !state.isLoading
                    ) { Text(t(Keys.refresh)) }
                }

                var showCancelDialog by remember { mutableStateOf(false) }
                val canCancel = sub is Subscription.Active && sub.isActive

                if (canCancel) {
                    OutlinedButton(
                        onClick = { showCancelDialog = true },
                        enabled = !state.isLoading,
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text(t(Keys.cancel_subscription))
                    }
                }

                if (showCancelDialog) {
                    AlertDialog(
                        onDismissRequest = { showCancelDialog = false },
                        title = { Text(t(Keys.cancel_subscription_confirm_title)) },
                        text = { Text(t(Keys.cancel_subscription_confirm_body)) },
                        confirmButton = {
                            Button(
                                onClick = {
                                    showCancelDialog = false
                                    vm.cancelSubscriptionAtPeriodEnd()
                                },
                                enabled = !state.isLoading,
                                colors = ButtonDefaults.buttonColors(containerColor = AppColors.Primary)
                            ) { Text(t(Keys.cancel_subscription_confirm_cta)) }
                        },
                        dismissButton = {
                            TextButton(
                                onClick = { showCancelDialog = false },
                                enabled = !state.isLoading
                            ) { Text(t(Keys.cancel_subscription_keep)) }
                        }
                    )
                }
            }
        }
        */

        OutlinedButton(
            onClick = { vm.logout(onLoggedOut) },
            enabled = !state.isLoading,
            modifier = Modifier.fillMaxWidth()
        ) { Text(t(Keys.logout)) }
    }
}